import React, { useEffect, useRef, useState } from 'react';
import Checkbox from './Checkbox';

const SCROLL_BOTTOM_THRESHOLD_PX = 8;

interface SignatureAgreementProps {
  content: string;
  checkboxLabel: string;
  checked: boolean;
  onValue: (checked: boolean) => void;
  onBlur?: () => void;
  error?: string[];
}

function isAtBottom(el: HTMLDivElement) {
  return el.scrollTop + el.clientHeight >= el.scrollHeight - SCROLL_BOTTOM_THRESHOLD_PX;
}

export default function SignatureAgreement(props: SignatureAgreementProps) {
  const { content, checkboxLabel, checked, onValue, onBlur, error } = props;
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(checked);
  const scrollRef = useRef<HTMLDivElement>(null);

  const errored = (error?.length ?? 0) > 0;

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    // The embed runs inside a shadow root (see designs/paper/index.tsx), where the
    // react-shadow-dom-retarget-events workaround re-dispatches React's synthetic events by
    // listening for them to bubble up to the shadow root. Native `scroll` events never bubble,
    // so React's `onScroll` prop can never fire here — attach a real listener directly instead.
    function handleScroll() {
      if (el && isAtBottom(el)) setHasScrolledToBottom(true);
    }

    handleScroll();
    el.addEventListener('scroll', handleScroll);
    return () => el.removeEventListener('scroll', handleScroll);
  }, [content]);

  return (
    <div className="w-full grid grid-cols-1 gap-2">
      <div
        ref={scrollRef}
        className={`html-view w-full rounded-lg border px-3 py-2 text-sm overflow-y-auto ${
          errored ? 'border-error' : 'border-border'
        }`}
        style={{ maxHeight: 220 }}
        dangerouslySetInnerHTML={{ __html: content }}
      />
      <Checkbox
        checked={checked}
        disabled={!hasScrolledToBottom}
        onBlur={onBlur}
        onValue={onValue}
        label={hasScrolledToBottom ? checkboxLabel : 'Scroll down to continue reading ↓'}
      />
      {errored && <div className="text-error text-sm">{error}</div>}
    </div>
  );
}
